// @flow
import type {Node} from 'react'
import React from 'react'
import {graphql, useFragment} from 'react-relay'
import liftNodes from 'utils/GraphQL/typedLiftNodes'
import Select from 'components/ui/forms/Select'

export type RoleTemplateFields = {
  name: string,
  purpose: string,
  domains: string[],
  accountabilities: string[],
}

type Props = {
  organization: any,
  roleTemplateId: ?string,
  onChange: (?(string | boolean), ?RoleTemplateFields) => void,
}

const organizationFragment = graphql`
  fragment RoleTemplateSelector_organization on Organization {
    onRestrictedPlan

    roleTemplates(first: 100) {
      edges {
        node {
          databaseId
          name
          purpose
          domains
          accountabilities
        }
      }
    }
  }
`

const defaultOptions = () => [
  {
    label: I18n.t('shared.none'),
    value: 'NONE',
  },
]

function RoleTemplateSelector({
  organization: organizationKey,
  roleTemplateId,
  onChange,
}: Props): Node {
  const organization = useFragment(organizationFragment, organizationKey)
  const roleTemplates = liftNodes(organization.roleTemplates)
  const isOnRestrictedPlan = organization.onRestrictedPlan

  if (!isOnRestrictedPlan && roleTemplates.length < 1)
    return null

  const roleTemplateOptions = roleTemplates.map((roleTemplate) => ({
    label: roleTemplate.name,
    value: roleTemplate.databaseId,
  }))

  const options = [...defaultOptions(), ...roleTemplateOptions]

  const onChangeHandler = (_, databaseId) => {
    const roleTemplate = roleTemplates.find((rt) => rt.databaseId === databaseId)

    if (!roleTemplate) {
      onChange(null, null)
      return
    }

    const payload = {
      name: roleTemplate.name,
      purpose: roleTemplate.purpose,
      domains: roleTemplate.domains,
      accountabilities: roleTemplate.accountabilities,
    }

    onChange(databaseId, payload)
  }

  return (
    <Select
      disabled={isOnRestrictedPlan}
      options={options}
      onChange={onChangeHandler}
      value={roleTemplateId || options[0].value}
      placeholder={I18n.t('side_nav.settings_section.role_templates.select_template')}
      premiumCalloutType={isOnRestrictedPlan && 'role_templates'}
      label={I18n.t('side_nav.settings_section.role_templates.choose_template')}
      name="roleTemplateId"
      tooltip={I18n.t('side_nav.settings_section.role_templates.select_tip')}
      test-id="role-template-select-form"
    />
  )
}

export default RoleTemplateSelector
